import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Per-card SM-2 spaced-repetition state, persisted to localStorage.
 *
 * A "card" is identified globally by `${jp}::${meaning}` — so the same vocab
 * item appearing in multiple lessons / wordlists shares its review state. A
 * vocab item with the same JP but a different meaning (e.g. 高い "tall" vs
 * 高い "expensive") is two distinct cards.
 *
 * Algorithm: classic SM-2 (SuperMemo) with Anki-style 4-button rating mapping
 * and Anki-style interval fuzzing to prevent cards clumping on the same day.
 *
 *   Again → quality 0, reset repetitions, due in 1 minute (re-show in session)
 *   Hard  → quality 3, gentle multiplier on the interval
 *   Good  → quality 4, standard SM-2 next interval
 *   Easy  → quality 5, larger multiplier
 *
 * State per card:
 *   repetitions  — count of consecutive successful (>=Hard) reviews
 *   easeFactor   — per-card difficulty multiplier (starts at 2.5, floor 1.3)
 *   interval     — current scheduling interval (days)
 *   dueAt        — epoch ms when the card next becomes due
 *   lastSeen     — epoch ms of last review
 */

const STORAGE_KEY = 'kotoba.cards.v1';

const ONE_MINUTE_MS = 60 * 1000;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export type CardStatus = {
  repetitions: number;
  easeFactor: number;
  interval: number; // days
  dueAt: number; // epoch ms
  lastSeen: number; // epoch ms; 0 if never reviewed
};

export type Rating = 0 | 1 | 2 | 3; // Again | Hard | Good | Easy

export function cardKey(jp: string, meaning: string): string {
  return `${jp}::${meaning}`;
}

const DEFAULT_STATUS: CardStatus = {
  repetitions: 0,
  easeFactor: 2.5,
  interval: 0,
  dueAt: 0,
  lastSeen: 0,
};

type AllCards = Record<string, CardStatus>;

type Ctx = {
  status: (key: string) => CardStatus;
  /** Returns the new status (so callers can use it without reading state). */
  rate: (key: string, rating: Rating) => CardStatus;
  isDue: (key: string, now?: number) => boolean;
  /** Reset a single card to never-seen state. */
  reset: (key: string) => void;
  /** Reset every card. */
  resetAll: () => void;
};

const CardStateContext = createContext<Ctx | null>(null);

function load(): AllCards {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: AllCards = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (!v || typeof v !== 'object') continue;
      const obj = v as Partial<CardStatus>;
      out[k] = {
        repetitions: typeof obj.repetitions === 'number' ? obj.repetitions : 0,
        easeFactor: typeof obj.easeFactor === 'number' ? obj.easeFactor : 2.5,
        interval: typeof obj.interval === 'number' ? obj.interval : 0,
        dueAt: typeof obj.dueAt === 'number' ? obj.dueAt : 0,
        lastSeen: typeof obj.lastSeen === 'number' ? obj.lastSeen : 0,
      };
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Apply Anki-style ±5–10% fuzz to an interval (days), to prevent cards from
 * clumping on the same day after a long pause. Fuzz only meaningful for
 * intervals > 2 days; shorter intervals stay deterministic so within-session
 * ordering doesn't get weird.
 */
function fuzzInterval(days: number): number {
  if (days <= 2) return days;
  const pct = days < 7 ? 0.05 : days < 30 ? 0.08 : 0.1;
  const noise = (Math.random() * 2 - 1) * pct;
  return days * (1 + noise);
}

/**
 * SM-2 next-schedule computation.
 *
 * Rating buttons map to SM-2 quality:
 *   Again (0) → q=0 (total blackout)
 *   Hard  (1) → q=3 (passed but with effort)
 *   Good  (2) → q=4 (clean recall)
 *   Easy  (3) → q=5 (trivial)
 *
 * Anki tweaks layered on top of vanilla SM-2:
 *   - Hard at rep>=2 uses a 1.2× multiplier instead of EF
 *   - Easy at rep<=1 graduates faster (4-day initial interval)
 *   - Failed cards re-show in the current session (60s) instead of 1 day
 */
export function nextSchedule(
  prev: CardStatus,
  rating: Rating,
  now: number = Date.now(),
): CardStatus {
  const q = rating === 0 ? 0 : rating === 1 ? 3 : rating === 2 ? 4 : 5;

  // Always update ease factor (SM-2 formula).
  const newEF = Math.max(
    1.3,
    prev.easeFactor + (0.1 - (5 - q) * (0.08 + (5 - q) * 0.02)),
  );

  if (q < 3) {
    // Failed — reset repetitions, show again in 1 minute (within session).
    return {
      repetitions: 0,
      easeFactor: newEF,
      interval: 0,
      dueAt: now + ONE_MINUTE_MS,
      lastSeen: now,
    };
  }

  // Successful: figure out the next interval.
  let nextDays: number;
  if (prev.repetitions === 0) {
    nextDays = q === 5 ? 4 : 1; // Easy graduates to 4 days; Hard/Good to 1 day.
  } else if (prev.repetitions === 1) {
    nextDays = q === 3 ? 3 : q === 5 ? 7 : 6; // Hard / Good / Easy
  } else {
    // Mature card.
    const mult = q === 3 ? 1.2 : q === 5 ? newEF * 1.3 : newEF;
    nextDays = prev.interval * mult;
  }

  const fuzzedDays = fuzzInterval(nextDays);

  return {
    repetitions: prev.repetitions + 1,
    easeFactor: newEF,
    interval: fuzzedDays,
    dueAt: now + fuzzedDays * ONE_DAY_MS,
    lastSeen: now,
  };
}

export function CardStateProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AllCards>(load);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const status = useCallback(
    (key: string): CardStatus => state[key] ?? DEFAULT_STATUS,
    [state],
  );

  const rate = useCallback((key: string, rating: Rating): CardStatus => {
    const now = Date.now();
    let computed: CardStatus = DEFAULT_STATUS;
    setState((prev) => {
      const current = prev[key] ?? DEFAULT_STATUS;
      computed = nextSchedule(current, rating, now);
      return { ...prev, [key]: computed };
    });
    return computed;
  }, []);

  const isDue = useCallback(
    (key: string, now: number = Date.now()) => {
      const s = state[key] ?? DEFAULT_STATUS;
      return s.dueAt <= now;
    },
    [state],
  );

  const reset = useCallback((key: string) => {
    setState((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const resetAll = useCallback(() => setState({}), []);

  const value = useMemo<Ctx>(
    () => ({ status, rate, isDue, reset, resetAll }),
    [status, rate, isDue, reset, resetAll],
  );

  return <CardStateContext.Provider value={value}>{children}</CardStateContext.Provider>;
}

export function useCardState(): Ctx {
  const ctx = useContext(CardStateContext);
  if (!ctx) throw new Error('useCardState must be used within CardStateProvider');
  return ctx;
}

/**
 * Map a card's repetitions count to a 0–5 visual "level" for the progress
 * bars in the flashcard UI. Just a UI affordance — the real state is in
 * `repetitions`, `easeFactor`, `interval`.
 */
export function visualLevel(status: CardStatus): number {
  return Math.min(5, status.repetitions);
}
